/**
 * Page identity and shared page state for the rankings Alpine app: basePath and iframe params,
 * breakpoint flags, the scoring modal, appData seeding, and the delegated body handlers (click
 * fan-out and the htmx table-swap orchestrator) that touch several concerns at once.
 *
 * Calls on sibling mixins via `this`: MobileTableChrome, TrendAndPlayerModals, UrlAndScoring,
 * GuestCta, ColumnVisibility, Sorting, UserLeague, RowLoading.
 */
import Alpine from '/js/alpine/core@3.14.9/alpine.esm.js';
import { mediaQueryManager, getIsMobile, getIsSmallScreen, getIsLargeScreen } from './../../helpers/Breakpoints.js';
import { DEFAULT_LOADING_TEXT } from './RankingsConstants.js';

export default () => ({
    basePath: '',
    // Params that must be appended to iframe requests, such as partnerId.
    persistentParams: {},
    // Iframe rankings should not push embedded filter changes into browser history.
    disableUrlUpdates: false,
    researchDepth: 'rankings',
    /** Index of selected research-depth segment. */
    toggleActive: 0,

    // Media Query Manager
    currentBreakpoint: null,
    currentBreakpointGroup: null,
    isMobile: false,
    isSmallScreen: false,
    isLargeScreen: false,

    // Scoring modal
    scoringModalOpen: false,
    loadingText: DEFAULT_LOADING_TEXT,

    // Guards the one-time window resize binding
    _ssrThumbResizeBound: false,

    initBreakpoints() {
        // Media Query Manager
        mediaQueryManager.init();
        this.currentBreakpoint = mediaQueryManager.currentBreakpoint;
        this.currentBreakpointGroup = mediaQueryManager.currentBreakpointGroup;
        this.isMobile = getIsMobile(mediaQueryManager.currentBreakpointGroup);
        this.isSmallScreen = getIsSmallScreen(mediaQueryManager.currentBreakpointGroup);
        this.isLargeScreen = getIsLargeScreen(mediaQueryManager.currentBreakpointGroup);

        mediaQueryManager.addListener((breakpoint, breakpointGroup) => {
            this.currentBreakpoint = breakpoint;
            this.currentBreakpointGroup = breakpointGroup;
            this.isMobile = getIsMobile(breakpointGroup);
            this.isSmallScreen = getIsSmallScreen(breakpointGroup);
            this.isLargeScreen = getIsLargeScreen(breakpointGroup);
            // The popover is desktop only. Close one that is open when the viewport leaves that.
            if (!this.isLargeScreen) {
                this.closeTrendPopover();
            }
            this.updateShowMobileTierLabel();
            this.refreshMobileTableChrome();
        });
    },

    initResizeSync() {
        if (!this._ssrThumbResizeBound) {
            this._ssrThumbResizeBound = true;
            let resizeRaf = null;
            window.addEventListener('resize', () => {
                if (resizeRaf) {
                    cancelAnimationFrame(resizeRaf);
                }
                resizeRaf = requestAnimationFrame(() => {
                    resizeRaf = null;
                    document.querySelectorAll('.btn-group--ssr .btn-group__control').forEach(groupEl => this.syncThumb(groupEl));
                    this.syncScrollSnapPadding();
                    this.syncGuestCtaViewportWidth();
                });
            });
        }
    },

    initAppData() {
        // Initialize default values from window.appData if available
        if (window.appData) {
            this.sortColumn = window.appData.sortColumn || this.sortColumn;
            this.pprScoring = window.appData.pprScoring || this.pprScoring;
            this.superflex = window.appData.superflex || this.superflex;
            this.rookiesOnly = window.appData.rookiesOnly || this.rookiesOnly;
            this.selectedFantasyPosition = window.appData.fantasyPosition?.toUpperCase() || this.selectedFantasyPosition;
            this.userLeagues = window.appData.userLeagues || [];
            this.leagueType = window.appData.leagueType || 'Redraft';
            this.columnGroups = window.appData.columnGroups || {};
            this.customColumnsOptions = window.appData.customColumnsOptions || this.customColumnsOptions;
            this.defaultMobileColumnAttributes = window.appData.defaultMobileColumnAttributes || [];
            this.defaultDesktopColumnAttributes = window.appData.defaultDesktopColumnAttributes || [];
            this.lowRangePoints = window.appData.lowRangePoints || {};
            this.highRangePoints = window.appData.highRangePoints || {};
            this.strengthOfSchedule = window.appData.strengthOfSchedule || [];
            this.selectedWeek = window.appData.week || null;
            this.currentWeek = window.appData.currentWeek || null;
            this.researchDepth = window.appData.researchDepth || this.researchDepth;
            if (!window.appData.basePath) {
                console.error('No basePath found in window.appData!');
                alert('Base path is required for rankings app to function correctly!');
            }
            this.basePath = window.appData.basePath;
            // Iframe renders provide these so shared HTMX/fetch requests stay authenticated and URL-stable.
            this.persistentParams = window.appData.persistentParams || {};
            this.disableUrlUpdates = window.appData.disableUrlUpdates || false;
            if (['weekly-rankings', 'ros-rankings'].includes(this.basePath)) {
                this.exportContainerOptionPrint = false;
            }
            this.customColumnsLocalStorageKey = `ds-${this.basePath}-custom-columns`;
        }
    },

    // One delegated listener: the early returns below gate the later branches, so the
    // modal, detail-row, export-menu and print handling stay together on purpose
    initDelegatedClicks() {
        document.body.addEventListener('click', e => {
            // Click and tap open the trend modal. Hover, further down, opens the popover.
            const trendCell = e.target.closest && e.target.closest('.rankings-trend-cell');
            if (trendCell) {
                e.preventDefault();
                this.openTrendModalFromCell(trendCell);
                return;
            }
            // Detail-row "View Player Details" still uses this class
            const openPlayerModalButton = e.target.closest('.open-player-modal');
            if (openPlayerModalButton) {
                const tbody = openPlayerModalButton.closest('tbody[data-player-row]');
                if (!tbody) {
                    console.warn('No player row found for button:', openPlayerModalButton);
                    return;
                }
                e.preventDefault();
                // Read the id now: the row can be swapped out while the modal code loads
                this.openPlayerModal(tbody.dataset.key, openPlayerModalButton);
            }
            // Expand player detail row when clicking on an element with class 'detail-row-expand-button'
            const expandPlayerDetailButton = e.target.closest('.detail-row-expand-button');

            const innerCloseDetailRowButton = e.target.closest('.detail-row-close-button');
            if (innerCloseDetailRowButton) {
                const detailRow = innerCloseDetailRowButton.closest('tr');
                if (detailRow) {
                    detailRow.classList.toggle('expanded');
                    const expandButton = detailRow.previousElementSibling.querySelector('.detail-row-expand-button');
                    if (expandButton) {
                        expandButton.classList.remove('expanded', 'active');
                        expandButton.setAttribute('aria-expanded', 'false');
                    }
                }
            }

            if (expandPlayerDetailButton) {
                const detailRow = expandPlayerDetailButton.closest('tr').nextElementSibling;
                if (detailRow) {
                    detailRow.classList.toggle('expanded');
                    const isExpanded = expandPlayerDetailButton.classList.toggle('expanded');
                    expandPlayerDetailButton.classList.toggle('active', isExpanded);
                    expandPlayerDetailButton.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
                }
                return;
            }

            const exportButton = e.target.closest('.menu-item.export-button');
            const exportContainer = e.target.closest('#export-container');
            if (!exportContainer && !exportButton && this.exportContainerOpen) {
                this.exportContainerOpen = false;
            }

            const printPageButton = e.target.closest('#printPageButton');
            if (printPageButton) {
                e.preventDefault();
                window.print();
            }
        });
    },

    initTableSwapHandler() {
        document.body.addEventListener('htmx:afterSwap', e => {
            if (!e.target.closest('#rankingsTableContainer')) {
                return; // Only handle swaps within the rankings table
            }

            // Position changes fire load-table with swap:'none', which still lands here.
            // Only a real swap (new #rankingsTable element) means parked rows now hold
            // stale markup - drop them then; load-rows refetches the full set below.
            const tableEl = document.querySelector('#rankingsTable');
            if (tableEl !== this._lastTableEl) {
                this._lastTableEl = tableEl;
                this._parkedRows = [];
            }

            // Depth/scoring swaps re-render thead and rows with SSR inline display styles
            this.updateColumnVisibilityStyles();
            this.stripInlineColumnDisplay(document.querySelector('#rankingsTable'));

            // Rankings-tab league apply runs before returning cells exist. Re-apply on
            // the swapped projections markup from cache (no refetch).
            if (this.selectedUserLeagueId && this.cachedLeagueData[this.selectedUserLeagueId]) {
                this.ensureDsValueForUserLeague(this.selectedUserLeagueId);
            }

            this.refreshMobileTableChrome();
            // The cell the popover was anchored to is gone after a table swap.
            this.closeTrendPopover();

            // Swap means we've changed scoring or research depth and have reloaded the table. These swaps return the 250
            // visiblle rows, so we now load the remaining unseen rows.
            this.loadRows(250, 0, 0, false);
            // Update visibility and remove duplicate tiers from mid-tier partial swaps
            Alpine.nextTick(() => {
                this.preserveTopScroll(() => this.refreshTable());
                this.scoringModalOpen = false;
            });
        });
    },
});
